import { api } from "@/redux/api";

import {
	AiTestRequest,
	AiTestResponse,
	AiTestResponseSchema,
	BulkCreateSalesforceFieldMappings,
	CreateSalesforceFieldMapping,
	CreateSalesforceUserMapping,
	SalesforceCompanySyncTriggerResponse,
	SalesforceCompanySyncTriggerResponseSchema,
	SalesforceEntityType,
	SalesforceEnvironment,
	SalesforceFieldMapping,
	SalesforceFieldMappingsResponse,
	SalesforceFieldMappingsResponseSchema,
	SalesforceFieldsResponse,
	SalesforceFieldsResponseSchema,
	SalesforceListSyncTriggerResponse,
	SalesforceListSyncTriggerResponseSchema,
	SalesforceOAuthConnectResponse,
	SalesforceOAuthConnectResponseSchema,
	SalesforceOAuthDisconnectResponse,
	SalesforceOAuthDisconnectResponseSchema,
	SalesforceOAuthStatusResponse,
	SalesforceOAuthStatusResponseSchema,
	SalesforceOwnersResponse,
	SalesforceOwnersResponseSchema,
	SalesforceResyncResponse,
	SalesforceResyncResponseSchema,
	SalesforceSourceFieldsResponse,
	SalesforceSourceFieldsResponseSchema,
	SalesforceSyncLogsResponse,
	SalesforceSyncLogsResponseSchema,
	SalesforceUserMapping,
	SalesforceUserMappingsResponse,
	SalesforceUserMappingsResponseSchema,
	UpdateSalesforceCompanySyncTrigger,
	UpdateSalesforceFieldMapping,
	UpdateSalesforceListSyncTrigger,
} from "../../pages/Company/schema/salesforce";
import {
	SalesforceFieldMappingPreviewRequest,
	SalesforceFieldMappingPreviewResponse,
	SalesforceFieldMappingPreviewResponseSchema,
	SalesforceTestSyncRequest,
	SalesforceTestSyncResponse,
	SalesforceTestSyncResponseSchema,
} from "../../pages/Company/schema/salesforceMappingPreview";

/**
 * OAuth, field discovery, mappings, owners, sync triggers and logs.
 *
 * Deal/lead configuration, pipelines and conversation sync live in `salesforceDeal.api.ts` —
 * Salesforce has strictly more endpoints than Dynamics (duplicate policy, defaults, preview,
 * test sync) and `dynamics.api.ts` is already 467 lines. Both files inject into the same slice
 * and share the `"Salesforce"` tag, so a mutation in one still invalidates the other's queries.
 */
export const salesforceApi = api.injectEndpoints({
	endpoints: (builder) => ({
		// ── Field discovery ──────────────────────────────────────────
		getSalesforceContactFields: builder.query<SalesforceFieldsResponse, void>({
			query: () => ({
				url: "salesforce/fields/contacts",
				method: "GET",
				responseSchema: SalesforceFieldsResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		getSalesforceAccountFields: builder.query<SalesforceFieldsResponse, void>({
			query: () => ({
				url: "salesforce/fields/accounts",
				method: "GET",
				responseSchema: SalesforceFieldsResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		getSalesforceLeadFields: builder.query<SalesforceFieldsResponse, void>({
			query: () => ({
				url: "salesforce/fields/leads",
				method: "GET",
				responseSchema: SalesforceFieldsResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		getSalesforceOpportunityFields: builder.query<SalesforceFieldsResponse, void>({
			query: () => ({
				url: "salesforce/fields/opportunities",
				method: "GET",
				responseSchema: SalesforceFieldsResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		getSalesforceSourceFields: builder.query<SalesforceSourceFieldsResponse, void>({
			query: () => ({
				url: "salesforce/source-fields",
				method: "GET",
				responseSchema: SalesforceSourceFieldsResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),

		// ── Field mapping CRUD ───────────────────────────────────────
		getSalesforceFieldMappings: builder.query<
			SalesforceFieldMappingsResponse,
			{ entityType?: SalesforceEntityType }
		>({
			query: (params) => ({
				url: "salesforce/field-mappings",
				method: "GET",
				params,
				responseSchema: SalesforceFieldMappingsResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		createSalesforceFieldMapping: builder.mutation<
			SalesforceFieldMapping,
			CreateSalesforceFieldMapping
		>({
			query: (body) => ({ url: "salesforce/field-mappings", method: "POST", body }),
			invalidatesTags: ["Salesforce"],
		}),
		updateSalesforceFieldMapping: builder.mutation<
			SalesforceFieldMapping,
			{ id: number; data: UpdateSalesforceFieldMapping }
		>({
			query: ({ id, data }) => ({
				url: `salesforce/field-mappings/${id}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["Salesforce"],
		}),
		deleteSalesforceFieldMapping: builder.mutation<{ success: boolean }, number>({
			query: (id) => ({ url: `salesforce/field-mappings/${id}`, method: "DELETE" }),
			invalidatesTags: ["Salesforce"],
		}),
		bulkCreateSalesforceFieldMappings: builder.mutation<
			SalesforceFieldMappingsResponse,
			BulkCreateSalesforceFieldMappings
		>({
			query: (body) => ({ url: "salesforce/field-mappings/bulk", method: "POST", body }),
			invalidatesTags: ["Salesforce"],
		}),
		/**
		 * Adds the out-of-the-box mappings the company is missing (a fresh connection is seeded
		 * with the same set). Returns only the created rows, so an empty array means every
		 * default is already configured.
		 */
		restoreSalesforceDefaultFieldMappings: builder.mutation<
			SalesforceFieldMappingsResponse,
			{ entityType?: SalesforceEntityType }
		>({
			query: (params) => ({
				url: "salesforce/field-mappings/defaults",
				method: "POST",
				params,
				responseSchema: SalesforceFieldMappingsResponseSchema,
			}),
			invalidatesTags: ["Salesforce"],
		}),

		// ── Mapping verification ─────────────────────────────────────
		/** Dry run: resolves the tab's mappings against one prospect without writing anything. */
		previewSalesforceFieldMappings: builder.mutation<
			SalesforceFieldMappingPreviewResponse,
			SalesforceFieldMappingPreviewRequest
		>({
			query: (body) => ({
				url: "salesforce/field-mappings/preview",
				method: "POST",
				body,
				responseSchema: SalesforceFieldMappingPreviewResponseSchema,
			}),
		}),
		/**
		 * Real write: runs the actual sync for one prospect and reports Salesforce's answer.
		 * Charges AI credits exactly as production does — a variant that suppressed the AI
		 * mappings would stop proving the payload the real sync writes.
		 */
		testSalesforceSync: builder.mutation<SalesforceTestSyncResponse, SalesforceTestSyncRequest>({
			query: (body) => ({
				url: "salesforce/sync/test",
				method: "POST",
				body,
				responseSchema: SalesforceTestSyncResponseSchema,
			}),
			invalidatesTags: ["Salesforce"],
		}),
		previewSalesforceAiVariables: builder.mutation<
			{ resolvedPrompt: string; variables: Record<string, string> },
			{ prospectId: number; prompt: string }
		>({
			query: (body) => ({
				url: "salesforce/field-mappings/ai/preview-variables",
				method: "POST",
				body,
			}),
		}),
		testSalesforceAiFieldMapping: builder.mutation<AiTestResponse, AiTestRequest>({
			query: (body) => ({
				url: "salesforce/field-mappings/ai/test",
				method: "POST",
				body,
				responseSchema: AiTestResponseSchema,
			}),
		}),

		// ── Sync trigger — company ───────────────────────────────────
		getSalesforceCompanySyncTrigger: builder.query<SalesforceCompanySyncTriggerResponse, void>({
			query: () => ({
				url: "salesforce/sync-trigger/company",
				method: "GET",
				responseSchema: SalesforceCompanySyncTriggerResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		updateSalesforceCompanySyncTrigger: builder.mutation<
			SalesforceCompanySyncTriggerResponse,
			UpdateSalesforceCompanySyncTrigger
		>({
			query: (body) => ({ url: "salesforce/sync-trigger/company", method: "PUT", body }),
			invalidatesTags: ["Salesforce", "Me", "Company"],
		}),

		// ── Sync trigger — list ──────────────────────────────────────
		getSalesforceListSyncTrigger: builder.query<SalesforceListSyncTriggerResponse, number>({
			query: (listId) => ({
				url: `salesforce/sync-trigger/list/${listId}`,
				method: "GET",
				responseSchema: SalesforceListSyncTriggerResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		updateSalesforceListSyncTrigger: builder.mutation<
			SalesforceListSyncTriggerResponse,
			{ listId: number; data: UpdateSalesforceListSyncTrigger }
		>({
			query: ({ listId, data }) => ({
				url: `salesforce/sync-trigger/list/${listId}`,
				method: "PUT",
				body: data,
			}),
			invalidatesTags: ["Salesforce"],
		}),

		// ── Owners & user mappings ───────────────────────────────────
		getSalesforceOwners: builder.query<SalesforceOwnersResponse, void>({
			query: () => ({
				url: "salesforce/owners",
				method: "GET",
				responseSchema: SalesforceOwnersResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		getSalesforceUserMappings: builder.query<SalesforceUserMappingsResponse, void>({
			query: () => ({
				url: "salesforce/user-mappings",
				method: "GET",
				responseSchema: SalesforceUserMappingsResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		createSalesforceUserMapping: builder.mutation<
			SalesforceUserMapping,
			CreateSalesforceUserMapping
		>({
			query: (body) => ({ url: "salesforce/user-mappings", method: "POST", body }),
			invalidatesTags: ["Salesforce"],
		}),
		deleteSalesforceUserMapping: builder.mutation<{ success: boolean }, number>({
			query: (id) => ({ url: `salesforce/user-mappings/${id}`, method: "DELETE" }),
			invalidatesTags: ["Salesforce"],
		}),

		// ── OAuth ────────────────────────────────────────────────────
		/**
		 * Not a bare link: Salesforce has no single OAuth host, so the environment is chosen up
		 * front and carried through the signed state. `myDomain` is required — and validated as
		 * a bare `https://<host>.my.salesforce.com` origin — only for `MY_DOMAIN`.
		 */
		getSalesforceOAuthConnectUrl: builder.query<
			SalesforceOAuthConnectResponse,
			{ environment: SalesforceEnvironment; myDomain?: string }
		>({
			query: ({ environment, myDomain }) => ({
				url: "salesforce/oauth/connect",
				method: "GET",
				params: myDomain ? { environment, myDomain } : { environment },
				responseSchema: SalesforceOAuthConnectResponseSchema,
			}),
		}),
		getSalesforceOAuthStatus: builder.query<SalesforceOAuthStatusResponse, void>({
			query: () => ({
				url: "salesforce/oauth/status",
				method: "GET",
				responseSchema: SalesforceOAuthStatusResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
		disconnectSalesforceOAuth: builder.mutation<SalesforceOAuthDisconnectResponse, void>({
			query: () => ({
				url: "salesforce/oauth",
				method: "DELETE",
				responseSchema: SalesforceOAuthDisconnectResponseSchema,
			}),
			invalidatesTags: ["Salesforce", "Me", "Company"],
		}),

		// ── Manual resync & sync logs ────────────────────────────────
		triggerSalesforceResync: builder.mutation<SalesforceResyncResponse, void>({
			query: () => ({
				url: "salesforce/sync/resync",
				method: "POST",
				responseSchema: SalesforceResyncResponseSchema,
			}),
			invalidatesTags: ["Salesforce"],
		}),
		getSalesforceSyncLogs: builder.query<
			SalesforceSyncLogsResponse,
			{ entityType?: string; limit?: number; offset?: number } | void
		>({
			query: (params) => ({
				url: "salesforce/sync/logs",
				method: "GET",
				params: params || undefined,
				responseSchema: SalesforceSyncLogsResponseSchema,
			}),
			providesTags: ["Salesforce"],
		}),
	}),
});

export const {
	useGetSalesforceContactFieldsQuery,
	useGetSalesforceAccountFieldsQuery,
	useGetSalesforceLeadFieldsQuery,
	useGetSalesforceOpportunityFieldsQuery,
	useGetSalesforceSourceFieldsQuery,
	useGetSalesforceFieldMappingsQuery,
	useCreateSalesforceFieldMappingMutation,
	useUpdateSalesforceFieldMappingMutation,
	useDeleteSalesforceFieldMappingMutation,
	useBulkCreateSalesforceFieldMappingsMutation,
	useRestoreSalesforceDefaultFieldMappingsMutation,
	usePreviewSalesforceFieldMappingsMutation,
	useTestSalesforceSyncMutation,
	usePreviewSalesforceAiVariablesMutation,
	useTestSalesforceAiFieldMappingMutation,
	useGetSalesforceCompanySyncTriggerQuery,
	useUpdateSalesforceCompanySyncTriggerMutation,
	useGetSalesforceListSyncTriggerQuery,
	useUpdateSalesforceListSyncTriggerMutation,
	useGetSalesforceOwnersQuery,
	useGetSalesforceUserMappingsQuery,
	useCreateSalesforceUserMappingMutation,
	useDeleteSalesforceUserMappingMutation,
	useLazyGetSalesforceOAuthConnectUrlQuery,
	useGetSalesforceOAuthStatusQuery,
	useDisconnectSalesforceOAuthMutation,
	useTriggerSalesforceResyncMutation,
	useGetSalesforceSyncLogsQuery,
} = salesforceApi;

export const salesforceApiReducer = salesforceApi.reducer;
export const salesforceApiMiddleware = salesforceApi.middleware;
